<template>
    <div class="scanner-overlay">
        <video
            ref="scannerVideo"
            class="scanner-video"
            autoplay
            muted
            playsinline
        ></video>
        <div class="scanner-shade"></div>
        <div class="scanner-mask scanner-mask-top"></div>
        <div class="scanner-mask scanner-mask-bottom"></div>
        <div class="scanner-mask scanner-mask-left"></div>
        <div class="scanner-mask scanner-mask-right"></div>

        <button v-active-touch class="scanner-close interactive-icon" type="button" :aria-label="$t('m.closeScanner')" @click="closeScanner">
            <img class="scanner-close-icon" :src="closeIcon" alt="">
        </button>
        <div class="scanner-title">{{ $t("m.scan") }}</div>
        <div class="scanner-hint">{{ $t("m.scannerHint") }}</div>

        <div class="scanner-frame" aria-hidden="true">
            <svg class="scanner-frame-corners" viewBox="0 0 239 239" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M80 0H10C4.477 0 0 4.477 0 10V80H4V10C4 6.686 6.686 4 10 4H80V0Z" fill="var(--theme-selectable-accent-color, #276CE1)"/>
                <path d="M159 0H229C234.523 0 239 4.477 239 10V80H235V10C235 6.686 232.314 4 229 4H159V0Z" fill="var(--theme-selectable-accent-color, #276CE1)"/>
                <path d="M80 239H10C4.477 239 0 234.523 0 229V159H4V229C4 232.314 6.686 235 10 235H80V239Z" fill="var(--theme-selectable-accent-color, #276CE1)"/>
                <path d="M159 239H229C234.523 239 239 234.523 239 229V159H235V229C235 232.314 232.314 235 229 235H159V239Z" fill="var(--theme-selectable-accent-color, #276CE1)"/>
            </svg>
            <span class="scanner-line"></span>
        </div>

        <button
            v-active-touch
            class="scanner-flashlight interactive-text"
            :class="{ active: isTorchOn, disabled: !torchSupported }"
            type="button"
            @click="toggleFlashlight"
        >
            <span class="scanner-flashlight-icon" :style="{ '--flashlight-icon': `url(${flashlightIcon})` }" aria-hidden="true"></span>
            <span>{{ $t("m.flashlight") }}</span>
        </button>

        <div v-if="scannerError" class="scanner-error">
            {{ scannerError }}
        </div>
    </div>
</template>

<script>
import { BrowserMultiFormatReader } from "@zxing/browser";
import flashlightIcon from "@/assets/map/flashlight.svg";
import closeIcon from "@/assets/map/close.svg";

export default {
    name: "ScannerOverlay",
    props: {
        scanParser: {
            type: Function,
            default: null,
        },
        invalidScanMessage: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            scannerReader: null,
            scannerControls: null,
            scannerError: "",
            scannerErrorTimer: null,
            scannerLastRejectedText: "",
            isTorchOn: false,
            torchSupported: false,
        };
    },
    computed: {
        flashlightIcon() {
            return flashlightIcon;
        },
        closeIcon() {
            return closeIcon;
        },
    },
    mounted() {
        this.startScanner();
    },
    beforeDestroy() {
        this.stopScanner();
        if (this.scannerErrorTimer) {
            clearTimeout(this.scannerErrorTimer);
            this.scannerErrorTimer = null;
        }
    },
    methods: {
        async startScanner() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.showScannerError(this.$t("m.cameraUnavailable"));
                return;
            }

            try {
                this.scannerReader = this.scannerReader || new BrowserMultiFormatReader();
                this.scannerControls = await this.scannerReader.decodeFromConstraints(
                    {
                        audio: false,
                        video: {
                            facingMode: { ideal: "environment" },
                        },
                    },
                    this.$refs.scannerVideo,
                    (result) => {
                        if (result) {
                            this.handleScanResult(result.getText());
                        }
                    }
                );
                window.setTimeout(() => this.syncTorchSupport(), 200);
            } catch (error) {
                this.showScannerError(error && error.name === "NotAllowedError"
                    ? this.$t("m.cameraPermissionDenied")
                    : this.$t("m.cameraStartFailed"));
            }
        },
        stopScanner() {
            if (this.scannerControls) {
                this.scannerControls.stop();
                this.scannerControls = null;
            }

            const video = this.$refs.scannerVideo;
            const stream = video && video.srcObject;
            if (stream && stream.getTracks) {
                stream.getTracks().forEach((track) => track.stop());
                video.srcObject = null;
            }

            this.isTorchOn = false;
            this.torchSupported = false;
        },
        closeScanner() {
            this.stopScanner();
            this.$emit("close");
        },
        getScannerVideoTrack() {
            const video = this.$refs.scannerVideo;
            const stream = video && video.srcObject;
            const tracks = stream && stream.getVideoTracks ? stream.getVideoTracks() : [];
            return tracks[0];
        },
        syncTorchSupport() {
            const track = this.getScannerVideoTrack();
            const capabilities = track && track.getCapabilities ? track.getCapabilities() : {};
            this.torchSupported = Boolean(capabilities && capabilities.torch);
        },
        async toggleFlashlight() {
            const track = this.getScannerVideoTrack();
            if (!track || !this.torchSupported || !track.applyConstraints) {
                this.showScannerError(this.$t("m.flashlightUnsupported"));
                return;
            }

            try {
                const nextTorchState = !this.isTorchOn;
                await track.applyConstraints({
                    advanced: [{ torch: nextTorchState }],
                });
                this.isTorchOn = nextTorchState;
            } catch (_) {
                this.showScannerError(this.$t("m.flashlightSwitchFailed"));
            }
        },
        showScannerError(message) {
            this.scannerError = message;
            if (this.scannerErrorTimer) {
                clearTimeout(this.scannerErrorTimer);
            }
            this.scannerErrorTimer = setTimeout(() => {
                this.scannerError = "";
                this.scannerLastRejectedText = "";
                this.scannerErrorTimer = null;
            }, 1800);
        },
        handleScanResult(text) {
            const rawText = String(text || "").trim();
            if (!rawText) return;

            if (!this.scanParser) {
                this.$emit("scan", rawText);
                return;
            }

            let parsedText = "";
            try {
                parsedText = this.scanParser(rawText);
            } catch (_) {
                parsedText = "";
            }

            if (!parsedText) {
                if (rawText === this.scannerLastRejectedText) return;
                this.scannerLastRejectedText = rawText;
                this.showScannerError(this.invalidScanMessage || this.$t("m.invalidQrCode"));
                return;
            }

            this.scannerLastRejectedText = "";
            this.$emit("scan", String(parsedText).trim());
        },
    },
};
</script>

<style lang="scss" scoped>
.scanner-overlay {
    position: fixed;
    inset: 0;
    z-index: 1003;
    overflow: hidden;
    background: #000;
    color: #fff;
    font-family: SF Pro Text, Avenir, Helvetica, Arial, sans-serif;
}

.scanner-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
}

.scanner-shade {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.18);
    pointer-events: none;
    z-index: 1;
}

.scanner-mask {
    position: absolute;
    background: rgba(0, 0, 0, 0.36);
    pointer-events: none;
    z-index: 2;
}

.scanner-mask-top {
    top: 0;
    left: 0;
    right: 0;
    height: calc(242px + env(safe-area-inset-top));
}

.scanner-mask-bottom {
    top: calc(477px + env(safe-area-inset-top));
    left: 0;
    right: 0;
    bottom: 0;
}

.scanner-mask-left {
    top: calc(242px + env(safe-area-inset-top));
    left: 0;
    width: calc((100% - 235px) / 2);
    height: 235px;
}

.scanner-mask-right {
    top: calc(242px + env(safe-area-inset-top));
    right: 0;
    width: calc((100% - 235px) / 2);
    height: 235px;
}

.scanner-close {
    position: absolute;
    top: calc(58px + env(safe-area-inset-top));
    left: 16px;
    z-index: 5;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.scanner-close-icon {
    width: 18px;
    height: 18px;
}

.scanner-title {
    position: absolute;
    top: calc(64px + env(safe-area-inset-top));
    left: 0;
    right: 0;
    z-index: 4;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    line-height: 23px;
    text-align: center;
}

.scanner-hint {
    position: absolute;
    top: calc(157px + env(safe-area-inset-top));
    left: 24px;
    right: 24px;
    z-index: 4;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    text-align: center;
}

.scanner-frame {
    position: absolute;
    top: calc(242px + env(safe-area-inset-top));
    left: 50%;
    z-index: 4;
    width: 235px;
    height: 235px;
    transform: translateX(-50%);
}

.scanner-frame-corners {
    position: absolute;
    left: -4px;
    top: -4px;
    width: 243px;
    height: 243px;
    pointer-events: none;
}

.scanner-line {
    position: absolute;
    top: 0;
    left: 50%;
    width: 198px;
    height: 2px;
    transform: translate(-50%, 70px);
    background: var(--theme-selectable-accent-color, #276ce1);
    box-shadow: 0 0 10px rgba(39, 108, 225, 0.65);
    animation: scannerLineScan 1.7s ease-in-out infinite alternate;
}

.scanner-flashlight {
    position: absolute;
    top: calc(602px + env(safe-area-inset-top));
    left: 50%;
    z-index: 4;
    min-width: 96px;
    border: 0;
    background: transparent;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    transform: translateX(-50%);
    padding: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
}

.scanner-flashlight.disabled {
    opacity: 0.6;
}

.scanner-flashlight.active {
    color: #fff;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.55);
}

.scanner-flashlight.touch-active {
    transform: translateX(-50%);
}

.scanner-flashlight.active::before {
    content: "";
    position: absolute;
    top: -7px;
    left: 50%;
    width: 34px;
    height: 34px;
    transform: translateX(-50%);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    box-shadow:
        0 0 10px rgba(255, 255, 255, 0.75),
        0 0 22px rgba(255, 255, 255, 0.42);
    pointer-events: none;
}

.scanner-flashlight-icon {
    position: relative;
    z-index: 1;
    width: 13px;
    height: 21px;
    display: block;
    background: rgba(255, 255, 255, 0.78);
    -webkit-mask: var(--flashlight-icon) center / contain no-repeat;
    mask: var(--flashlight-icon) center / contain no-repeat;
}

.scanner-flashlight.active .scanner-flashlight-icon {
    background: #fff;
    filter:
        drop-shadow(0 0 5px rgba(255, 255, 255, 0.95))
        drop-shadow(0 0 12px rgba(255, 255, 255, 0.7));
}

.scanner-error {
    position: absolute;
    top: calc(500px + env(safe-area-inset-top));
    left: 50%;
    z-index: 6;
    width: min(296px, calc(100vw - 40px));
    padding: 12px 16px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba(17, 20, 27, 0.78);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    line-height: 19px;
    text-align: center;
    transform: translateX(-50%);
    pointer-events: none;
}

@keyframes scannerLineScan {
    from {
        transform: translate(-50%, 70px);
    }

    to {
        transform: translate(-50%, 166px);
    }
}
</style>
